import * as gsf from "core/src/core/game-state-functions.js";
import { wormColors } from "core/src/core/constants.js";
import { followTrajectory } from "core/src/core/geometry/trajectory/trajectory-util.js";

import ScaledCanvasContext from "../scaled-canvas-context.js";
import { drawWormEffectBars, effectBarsExtentPx } from "../effect-bars";
import * as csf from "../canvas-state-functions.js";

import { drawHeadStyle, isHeadStyle, headStyleRadius, headStyleReach } from "../head-styles";
import { spawnedAt, SPAWN_SPOTLIGHT_DURATION } from "./spawn-events.js";
import { introAlpha } from "../../shared/round-intro";
import { drawVictoryAnim, drawVictoryCrash, isVictoryAnim } from "../victory-anim";
import { drawUpright } from "../arena-view";
import { getVictoryLapEvent as lapEventAt, getLapDeath, LapCrashClock } from "./lap-crash";

var HEAD_COLOR = "#FFB74D"; // matches the shared worm-head renderer's head colour
var KEY_SWITCH_HEAD_COLOR = "#3388BB"; // matches the shared worm-head renderer (key_switch)
// Drop-in pulse rings (mirrors worm-head-renderer's drawSpawnFx) so the LOCAL
// worm also throbs during the countdown and you can find it in a crowd. The shared
// head renderer excludes the local worm, so we draw its rings here.
var PULSE_INTERVAL = 0.45;
var PULSE_LIFETIME = 1.27;
var PULSE_LINE_WIDTH = 2;
var PULSE_MAX_DIST = 13.1;
var PULSE_START_GAP = 2;       // a ring is born this far (game units) outside the head's edge
var PULSE_ALPHA = 0.95;        // a ring's opacity when it is born (fades to 0 over its lifetime)
var PULSE_WASH = 0.6;          // the inward wash's opacity, relative to its ring's
// How far apart successive rings are (in head sizes): the wash behind a ring
// reaches exactly this far back, so it hands over to the next ring seamlessly.
var PULSE_SPACING = PULSE_MAX_DIST * PULSE_INTERVAL / PULSE_LIFETIME;
var SWITCH_SPOTLIGHT_DURATION = 1.5; // seconds the own head pulses after a switcharoonie (as in worm-head-renderer)

// ONLINE-ONLY renderer. Draws the local player's own worm's HEAD + FX from
// client-side prediction so steering feels instant. The worm's TRAIL - the predicted
// lead and its wall-hack ghost tracks included - is drawn by the one shared body
// renderer like any other worm's: GameCanvas resolves the own track's (and its "#"
// ghost tracks') per-worm render clock to the predicted target time, so that
// renderer's tail pass simply reaches the predicted head (the own worm isn't a
// special draw path, just a worm with a fresher clock). This renderer is added to
// the pipeline only in online play and draws onto the shared per-frame canvas AFTER
// the shared worm-head renderer (so the own head + FX land on top of every OTHER
// worm's head; the body renderer, first on that canvas, owns its per-frame clear).
// Offline play never constructs this, so the shared renderers carry no prediction
// logic.
export default function PredictedWormRenderer({ gameState, players, localPlayerId, predictor, getTargetTime, canvas, canvasState, targetTrackId = null, scale = 1, debug = false, introDelay, rotated = false }) {
    var context = canvas.getContext("2d");
    var scaledContext = ScaledCanvasContext(context, scale);

    // Dynamic-layer dirty rects (see worm-body-renderer): report drawn PIXEL regions,
    // or mark the frame unbounded (dynFull) for FX not worth bounding.
    function dynPushPx(x, y, r) {
        var d = canvasState && canvasState.dyn;
        if (d) d.cur.push([x - r, y - r, r * 2, r * 2]);
    }
    function dynFull() {
        var d = canvasState && canvasState.dyn;
        if (d) d.full = true;
    }
    function headExtentPx(size, headStyle) {
        return Math.max(
            headStyleRadius(size, headStyle) * scale * headStyleReach(headStyle) + 8,
            effectBarsExtentPx(size * scale)
        );
    }

    // The start-phase indicators (arrow, spawn pulse) are drawn in game-space
    // units and scale with the map's span, exactly as in the shared worm-head
    // renderer, so the own worm's arrow is the same size as everyone else's.
    var mapSpan = Math.max((gameState.map && gameState.map.width) || 0, (gameState.map && gameState.map.height) || 0);
    var startFxScale = Math.min(2.5, Math.max(1, mapSpan / 600));

    // The start-of-round launch arrow (mirrors the shared worm-head renderer's
    // drawArrow). The local worm is excluded from that renderer, so we draw its
    // arrow here while it's stationary in the start phase.
    function drawArrow(x, y, direction, size, color) {
        context.save();
        context.fillStyle = color;
        context.strokeStyle = color;
        context.lineWidth = scale * 4 * startFxScale;
        context.beginPath();
        scaledContext.translate(x, y);
        context.rotate(direction - Math.PI / 2);
        // Same geometry as the shared renderer: the shaft starts clear of the
        // head plus a gap, and the whole arrow scales with the map.
        var arrowStart = headStyleRadius(size) + 7;
        var arrowBaseEndpoint = arrowStart + 25 * startFxScale;
        var arrowHeadEndpoint = arrowStart + 30 * startFxScale;
        var arrowHeadWidth = 4 * startFxScale;
        scaledContext.moveTo(0, arrowStart);
        scaledContext.lineTo(0, arrowBaseEndpoint);
        scaledContext.lineTo(-arrowHeadWidth, arrowBaseEndpoint);
        scaledContext.lineTo(0, arrowHeadEndpoint);
        scaledContext.lineTo(arrowHeadWidth, arrowBaseEndpoint);
        scaledContext.lineTo(0, arrowBaseEndpoint);
        context.closePath();
        context.fill();
        context.stroke();
        context.restore();
    }

    function drawSpawnFx(x, y, size, color, time, gameStartTime) {
        if (time < 0) {
            return;
        }
        context.save();
        context.strokeStyle = color;
        context.lineWidth = scale * PULSE_LINE_WIDTH * startFxScale;
        var firstPulse = Math.max(0, Math.ceil((time - PULSE_LIFETIME) / PULSE_INTERVAL));
        var lastPulse = Math.floor(time / PULSE_INTERVAL);
        for (var i = firstPulse; i <= lastPulse; i++) {
            var pulseStartTime = i * PULSE_INTERVAL;
            if (pulseStartTime >= gameStartTime) {
                break; // no new pulses once the game starts
            }
            dynFull(); // expanding rings: full-clear next frame instead of bounding
            var fp = (time - pulseStartTime) / PULSE_LIFETIME; // 0..1 within a pulse
            // Born just outside the head (cosmetic heads draw bigger than the body),
            // never inside it.
            var R = headStyleRadius(size) + PULSE_START_GAP + size * PULSE_MAX_DIST * startFxScale * fp;
            var a = PULSE_ALPHA * (1 - fp);
            // A thin, strong ring with a wash of colour fading INWARD from it all the
            // way back to where the next ring follows (PULSE_SPACING), so the band
            // between two rings is one continuous wave: bright at the leading edge,
            // gone by the time the next ring takes over.
            var rPx = R * scale, inPx = Math.max(0, R - size * PULSE_SPACING * startFxScale) * scale;
            var g = context.createRadialGradient(x * scale, y * scale, inPx, x * scale, y * scale, rPx);
            g.addColorStop(0, "rgba(0,0,0,0)");
            g.addColorStop(1, color);
            context.globalAlpha = a * PULSE_WASH;
            context.fillStyle = g;
            context.beginPath();
            scaledContext.arc(x, y, R, 0, 2 * Math.PI);
            context.fill();
            context.globalAlpha = a;
            context.beginPath();
            scaledContext.arc(x, y, R, 0, 2 * Math.PI);
            context.stroke();
        }
        context.restore();
    }

    function drawHeadDot(x, y, size, direction, headShape, headColor) {
        context.save();
        // Cosmetic head (shop): YOUR own worm online is drawn here, not by the
        // shared head renderer, so the styled head must render here too.
        var me = players && players.find ? players.find(function (p) { return p.id === localPlayerId; }) : null;
        if (me && isHeadStyle(me.headStyle) && drawHeadStyle(context, x * scale, y * scale, headStyleRadius(size, me.headStyle) * scale, direction || 0, getTargetTime ? getTargetTime() : 0, me.headStyle, wormColors[me.colorId])) {
            context.restore();
            return;
        }
        context.fillStyle = headColor || HEAD_COLOR;
        if (headShape === "square") {
            // tronTurn ("turn 90 deg"): a square head, matching the shared worm-head
            // renderer so YOUR own worm shows the effect online too.
            scaledContext.translate(x, y);
            context.rotate((direction || 0) - Math.PI / 2);
            var hr = headStyleRadius(size);
            scaledContext.fillRect(-hr, -hr, hr * 2, hr * 2);
        } else {
            context.beginPath();
            scaledContext.arc(x, y, headStyleRadius(size), 0, 2 * Math.PI);
            context.fill();
        }
        context.restore();
    }

    // Head shape + colour for the local worm from its active effects. The shared
    // worm-head renderer does this for other worms; the predicted own-worm renderer
    // must too, or YOUR own head never shows the tronTurn square / key-switch colour.
    function ownHeadVisual() {
        // Your own worm shows its power-ups as status bars now (drawn after the head),
        // so its head no longer changes shape/colour for tronTurn / key_switch.
        return { shape: "circle", color: HEAD_COLOR };
    }

    // The victory lap and the winner's crash on it: lap-crash.js (shared with
    // the head renderer, which draws everyone else's).
    var lapCrashElapsed = LapCrashClock();
    function getVictoryLapEvent(time) {
        return lapEventAt(gameState, time);
    }
    // Your own crash shows the moment your own collision calls it (the host's
    // player_died event is a round trip behind), and once it does it holds even
    // though your worm's clock is pinned at the end of its track.
    function getOwnLapDeath(lapEvent, toT, trackEnd) {
        var stopped = !!(predictor && predictor.isDead && predictor.isDead());
        return getLapDeath(gameState, lapEvent, localPlayerId, toT, trackEnd, stopped);
    }

    // Your own celebration on the victory lap (online). The shared head renderer
    // draws every OTHER winner's; it excludes the local worm online, so without
    // this your own equipped celebration never showed. Same gate as there: only
    // when the winner is you and you own one (player.victoryAnim).
    function drawOwnVictory(me, x, y, size, color, time, trackEnd, frameTime) {
        var lapEvent = getVictoryLapEvent(time);
        if (!lapEvent || lapEvent.playerId !== localPlayerId || !me || !isVictoryAnim(me.victoryAnim)) return;
        dynFull(); // celebration FX: unbounded, full-clear next frame
        var death = getOwnLapDeath(lapEvent, time, trackEnd);
        var crashElapsed = death ? lapCrashElapsed(localPlayerId, lapEvent, frameTime) : 0;
        drawUpright(context, x * scale, y * scale, rotated, function () {
            (death ? drawVictoryCrash : drawVictoryAnim)(
                context,
                x * scale,
                y * scale,
                size * scale * startFxScale,
                color,
                me.flag,
                death ? crashElapsed : time - lapEvent.time,
                me.victoryAnim,
                death ? death.time - lapEvent.time : 0
            );
        });
    }

    // Mirrors worm-head-renderer.js drawDrunkBubbles for the local worm (the shared
    // renderer excludes it online, so its drunk visual would otherwise be missing).
    // Solid grey smoke balls, filled (see the shared renderer's note).
    function drawDrunkBubbles(wormId, time, segmentId) {
        context.save();
        context.globalAlpha = 0.3;
        context.fillStyle = "white";
        var bMinX = Infinity, bMinY = Infinity, bMaxX = -Infinity, bMaxY = -Infinity, bMaxR = 0;
        csf.getDrunkBubbles(gameState, time, segmentId, wormId).forEach(function (bubble) {
            if (bubble.x < bMinX) bMinX = bubble.x;
            if (bubble.x > bMaxX) bMaxX = bubble.x;
            if (bubble.y < bMinY) bMinY = bubble.y;
            if (bubble.y > bMaxY) bMaxY = bubble.y;
            if (bubble.radius > bMaxR) bMaxR = bubble.radius;
            context.beginPath();
            scaledContext.arc(bubble.x, bubble.y, bubble.radius, 0, 2 * Math.PI);
            context.fill();
        });
        if (bMinX !== Infinity && canvasState && canvasState.dyn) {
            var bPad = bMaxR * scale + 8; // stroke + antialiasing slack (big fat-worm bubbles showed edge remnants)
            canvasState.dyn.cur.push([
                bMinX * scale - bPad, bMinY * scale - bPad,
                (bMaxX - bMinX) * scale + bPad * 2, (bMaxY - bMinY) * scale + bPad * 2,
            ]);
        }
        context.restore();
    }

    // A head at the TIP of the mirror worm, taken from the SAME ghost segments the shared
    // body renderer draws the mirror body from - so the head is always exactly where the
    // mirror body is, never on the far side before the body has wrapped there. Only the
    // actively-extended mirror track gets a head (its tip is at ~now); older ghost tracks
    // are just trail history, so they get none.
    var GHOST_HEAD_GRACE = 0.05; // s: bridge the odd predicted frame whose last sub-step didn't straddle
    function drawGhostHeads(trackId, headVisual, size, toT) {
        var tracks = gameState.wormPathSegments;
        var prefix = trackId + "#";
        for (var id in tracks) {
            if (id.indexOf(prefix) !== 0) continue;
            var gsegs = tracks[id];
            if (!gsegs.length) continue;
            var last = gsegs[gsegs.length - 1];
            // Once the worm stops straddling, toT runs past the tip and the head clears - it never
            // lingers off the body (and the grace bridges a single non-straddling sub-step).
            if (toT > last.endTime + GHOST_HEAD_GRACE) continue;
            var pos = followTrajectory(last, Math.min(toT, last.endTime) - last.startTime);
            drawHeadDot(pos.x, pos.y, size, pos.direction, headVisual.shape, headVisual.color);
            dynPushPx(pos.x * scale, pos.y * scale, headExtentPx(size, null));
        }
    }

    // When the switcharoonie that handed us this worm went: the server's
    // switch_slow on it, or until that event lands, the slow prediction pushed
    // at go (online/prediction.ts, keyed by the go time). Null: no switch on.
    function ownSwitchTime(wormId, t) {
        var at = null;
        csf.getActiveEffects(gameState, t, { wormId: wormId, effectName: "switch_slow" }).forEach(function (e) {
            if (typeof e.time === "number" && (at === null || e.time > at)) at = e.time;
        });
        (gameState.effects || []).forEach(function (e) {
            if (e._predicted && e.type === "switchSlow" && e.wormId === wormId && typeof e._predictedGo === "number"
                && e._predictedGo <= t && t < e._predictedGo + SWITCH_SPOTLIGHT_DURATION + PULSE_LIFETIME
                && (at === null || e._predictedGo > at)) at = e._predictedGo;
        });
        return at;
    }

    function render(renderTime) {
        var targetTime = getTargetTime ? getTargetTime() : renderTime;
        // predictor.predict() extends ALL our worms' tracks and returns the primary's id.
        // We draw `targetTrackId` if given (this instance owns one specific worm - a
        // player can have two after a twin), else the primary.
        var primaryTrackId = predictor.predict(gameState, targetTime);
        var trackId = targetTrackId || primaryTrackId;
        if (!trackId) {
            return;
        }
        var segs = gameState.wormPathSegments[trackId];
        if (!segs || segs.length === 0) {
            return;
        }
        // A Respawn round: our crashed worm leaves with its trail (core respawn.js)
        if (segs[segs.length - 1].clearedAt !== undefined && segs[segs.length - 1].clearedAt <= renderTime) {
            return;
        }
        var player = players.find(function (p) { return p.id === localPlayerId; });
        if (!player) {
            return;
        }
        var size = segs[segs.length - 1].size;
        var headVisual = ownHeadVisual(segs[0].wormId);

        // Start phase: the worm is stationary (still_arc) but rotates as you aim.
        // Draw the arrow at the LATEST (real-time) direction the client owns, not
        // the delayed renderTime, so aiming during the countdown is lag-free.
        var last = segs[segs.length - 1];
        if (last.type === "still_arc") {
            dynFull(); // launch arrow (start phase): full-clear next frame
            // Roll call (round-intro.ts): the own head fades in on its beat.
            var startDelay = introDelay ? (introDelay(localPlayerId) || 0) : 0;
            var startIntro = introDelay ? introAlpha(renderTime, startDelay, gameState.startPhaseTime) : 1;
            if (startIntro <= 0) return;
            context.save();
            context.globalAlpha = startIntro;
            drawArrow(last.endX, last.endY, last.endDirection, size, wormColors[player.colorId]);
            drawSpawnFx(last.endX, last.endY, size, wormColors[player.colorId], renderTime - startDelay, gameState.startPhaseTime - startDelay);
            drawHeadDot(last.endX, last.endY, size, last.endDirection, headVisual.shape, headVisual.color);
            context.restore();
            return;
        }

        var toT = Math.min(targetTime, segs[segs.length - 1].endTime);
        var color = debug ? "#ff0000" : wormColors[player.colorId];

        // The trail - predicted lead and wall-hack ghost tracks included - is drawn by
        // the shared body renderer (GameCanvas resolves this track's and its ghost
        // tracks' per-worm clock to the predicted target time), so only heads + FX here.

        // Wall-hack mirror head at the ghost track's TIP, so head and body are one worm.
        drawGhostHeads(trackId, headVisual, size, toT);

        // Drunk bubbles. The shared head renderer draws these for every OTHER worm but
        // skips the local one online, so draw the own worm's here (same look), keyed off
        // the own real-time clock + predicted track so the bubble positions resolve.
        // A DEAD own worm's toT freezes at the track end, which would freeze in-flight
        // bubbles over the corpse; use the still-advancing target time instead so they
        // finish rising and vanish (spawns past the death are capped in getDrunkBubbles).
        // A victory-lap crash marks nobody dead, so also read the track's own
        // death marker and the lap death event.
        var bubbleWorm = gameState.worms.find(function (w) { return w.id === segs[0].wormId; });
        var lastOwnSeg = segs[segs.length - 1];
        var ownLap = getVictoryLapEvent(toT);
        var ownStopped = (bubbleWorm && bubbleWorm.alive === false)
            || (lastOwnSeg && lastOwnSeg.type === "worm_died")
            || !!(ownLap && getOwnLapDeath(ownLap, toT, lastOwnSeg.endTime));
        drawDrunkBubbles(segs[0].wormId, ownStopped ? targetTime : toT, trackId);

        // Head: always drawn at the worm's latest position, so it stays visible at
        // the crash point after death instead of vanishing when the clock passes it.
        var head = gsf.getWormPathSegmentDataAtTime(gameState, trackId, toT);
        if (head) {
            // Roll call (round-intro.ts): the own head fades in on its beat too.
            var delay = introDelay ? (introDelay(localPlayerId) || 0) : 0;
            var intro = introDelay ? introAlpha(renderTime, delay, gameState.startPhaseTime) : 1;
            if (intro <= 0) {
                dynFull();
                return;
            }
            context.save();
            context.globalAlpha = intro;
            // Fading drop-in pulses keep showing right after the game starts; they
            // start when the head appears, not before.
            drawSpawnFx(head.position.x, head.position.y, size, color, renderTime - delay, gameState.startPhaseTime - delay);
            // Switcharoonie: the same pulses on the worm you were just handed, so
            // you can find it. (The shared head renderer has this for the worms it
            // draws, but online it skips your own, which is drawn here.)
            var switchedAt = ownSwitchTime(segs[0].wormId, toT);
            if (switchedAt !== null) {
                drawSpawnFx(head.position.x, head.position.y, size, color, toT - switchedAt, SWITCH_SPOTLIGHT_DURATION);
            }
            // put in while the round is going (back in after a crash, dropping in)
            var putInAt = spawnedAt(gameState, segs[0].wormId);
            if (putInAt !== undefined) {
                drawSpawnFx(head.position.x, head.position.y, size, color, toT - putInAt, SPAWN_SPOTLIGHT_DURATION);
            }
            drawHeadDot(head.position.x, head.position.y, size, head.position.direction, headVisual.shape, headVisual.color);
            context.restore();
            // Active power-up status bars on YOUR own worm's head (online).
            drawWormEffectBars(context, gameState, segs[0].wormId, head.position.x, head.position.y, size, scale, toT);
            var me = players.find(function (p) { return p.id === localPlayerId; });
            // Your celebration through the victory lap (a mid-lap crash: its crash
            // sequence), on the own worm's clock like the head itself.
            drawOwnVictory(me, head.position.x, head.position.y, size, color, toT, segs[segs.length - 1].endTime, renderTime);
            // (where the own head is drawn this frame: the winner's spotlight follows it)
            if (canvasState && canvasState.heads) canvasState.heads["own:" + trackId] = { x: head.position.x * scale, y: head.position.y * scale, playerId: localPlayerId, at: renderTime };
            dynPushPx(head.position.x * scale, head.position.y * scale, headExtentPx(size, me && me.headStyle));
        }
    }

    return { render: render };
};
